import {
  sortMembers,
  useSortedMembers,
} from '@/app/(intakeFormV2)/intake-v2/hooks/useSortedMembers';
import { stateToUpdateRequest } from '@/app/(intakeFormV2)/intake-v2/hooks/useWizardSessionLifecycle';
import { initiateCheckout } from '@/app/(intakeFormV2)/intake-v2/services/CheckoutService';
import { calculatePrice } from '@/app/(intakeFormV2)/intake-v2/services/QuoteService';
import {
  Member,
  Plan,
  WizardState,
} from '@/app/(intakeFormV2)/intake-v2/types';
import { sanitizeMembers } from '@/app/(intakeFormV2)/intake-v2/utils/sanitizeMembers';
import type { SalesforceCalculatePriceResponse } from '@/types/salesforce';
import { isPediatricsEligible } from '@/utils/pediatrics';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateSession } from '../../../contexts/intakeSessionApi';
import { useIntakeWizardContext } from '../../../contexts/WizardProvider';
import { useWizardNavigation } from '../../../hooks/useWizardNavigation';

type TForceSaveToApi = {
  currentState: WizardState;
  quote: SalesforceCalculatePriceResponse;
};

const useForceSaveToApi = () => {
  const [loading, setLoading] = useState(false);

  const { update: updateSession } = useUpdateSession();

  const forceSave = useCallback(
    async ({ currentState, quote }: TForceSaveToApi) => {
      if (!currentState.sessionId) return;

      setLoading(true);

      await updateSession(currentState.sessionId, {
        ...stateToUpdateRequest(currentState),
        quote,
      }).then(() => {
        setLoading(false);
      });
    },
    [updateSession]
  );

  return { forceSave, loading };
};

const DEBOUNCE_DISCOUNT_CODE = 300;

const upgradePlatinum = (members: Member[]) =>
  members.map(member => ({
    ...member,
    plan: member.plan === 'Signature' ? 'Platinum' : member.plan,
  }));

const upgradePediatrics = (members: Member[], hasPediatrics: boolean) =>
  members.map(member => ({
    ...member,
    plan:
      hasPediatrics &&
      isPediatricsEligible(member.date_of_birth) &&
      member.plan === 'Signature'
        ? 'Pediatrics'
        : member.plan,
  }));

export const useQuoteData = () => {
  const { forceSave } = useForceSaveToApi();
  const router = useRouter();
  const { state, dispatch } = useIntakeWizardContext();
  const { goToStep } = useWizardNavigation();
  const [discountCode, setDiscountCode] = useState(
    state.formData.discount_code || ''
  );

  const [debouncedDiscountCode, setDebouncedDiscountCode] = useState(
    state.formData.discount_code || ''
  );

  const [appliedDiscountCode, setAppliedDiscountCode] = useState(
    state.formData.discount_code || ''
  );
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(true);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [quote, setQuote] = useState<SalesforceCalculatePriceResponse | null>(
    null
  );
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [membersReady, setMembersReady] = useState(false);

  // TODO -- refactor by using useMemo instead of useEffect [mandatory]
  // Pre-quote sanitization: fix primary assignment and sort order before pricing
  useEffect(() => {
    const currentMembers = state.formData.members ?? [];
    const sanitized = sortMembers(sanitizeMembers(currentMembers));

    const changed =
      sanitized.length !== currentMembers.length ||
      sanitized.some(
        (m, i) =>
          m.isPrimary !== currentMembers[i].isPrimary ||
          m.id !== currentMembers[i].id ||
          m.date_of_birth !== currentMembers[i].date_of_birth
      );

    if (changed) {
      dispatch({ type: 'SET_FORM_DATA', payload: { members: sanitized } });
    }

    setMembersReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce discount code input
  const handleDiscountCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDiscountCode(value);
      setDiscountError(null);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer (300ms)
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedDiscountCode(value);
      }, DEBOUNCE_DISCOUNT_CODE);
    },
    []
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Determine if Apply button should be enabled
  const isApplyButtonEnabled =
    debouncedDiscountCode.trim() !== '' &&
    debouncedDiscountCode.trim() !== appliedDiscountCode;

  // Mark pediatrics-upsell as completed when reaching the quote page
  useEffect(() => {
    if (!state.completedSteps.includes('pediatrics-upsell')) {
      dispatch({ type: 'COMPLETE_STEP', payload: 'pediatrics-upsell' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch quote once members have been sanitized
  useEffect(() => {
    if (!membersReady) return;

    const fetchQuote = async () => {
      setIsLoadingQuote(true);
      setQuoteError(null);

      try {
        const result = await calculatePrice(
          state.formData,
          state.formData.discount_code
        );
        setQuote(result);
        return result;
      } catch (error) {
        console.error('Error fetching quote:', error);
        setQuoteError('Failed to load your quote. Please try again.');
      } finally {
        setIsLoadingQuote(false);
      }
    };

    fetchQuote().then(quote => {
      if (!quote) return;

      forceSave({ currentState: state, quote });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersReady]);

  // Get member prices from quote response, matched by name
  const getMemberPrice = useCallback(
    (firstName: string, lastName: string): number | null => {
      if (!quote) return null;
      const fullName = `${firstName} ${lastName}`;
      const individual = quote.individualPrices.find(p => p.name === fullName);
      return individual?.price ?? null;
    },
    [quote]
  );

  const rawMembers = state.formData.members || [];
  const members = useSortedMembers(rawMembers);
  const totalPrice = quote?.overallPrice ?? 0;
  const discountedPrice = quote?.discountedPrice ?? 0;
  const isPromotionUsed = !!quote?.promotionUsed;
  const discountPercentage = quote?.promotionUsed?.percentageReduction ?? 0;
  const plans = state.formData.members?.map(x => x.plan ?? 'Signature') ?? [];

  const handleApplyDiscount = useCallback(async () => {
    const trimmedCode = debouncedDiscountCode.trim();
    if (!trimmedCode || trimmedCode === appliedDiscountCode) return;

    setIsApplyingDiscount(true);
    setQuoteError(null);
    setDiscountError(null);

    try {
      // Recalculate price with the promo code
      const result = await calculatePrice(state.formData, trimmedCode);
      setQuote(result);

      if (!result.promotionUsed) {
        setDiscountError('Sorry, that discount code is not valid.');
      }

      // Mark this code as applied
      setAppliedDiscountCode(trimmedCode);

      // Save discount code to state
      dispatch({
        type: 'SET_FORM_DATA',
        payload: { discount_code: trimmedCode },
      });
    } catch (error) {
      console.error('Error applying discount:', error);
      setQuoteError('Failed to apply discount code. Please try again.');
    } finally {
      setIsApplyingDiscount(false);
    }
  }, [debouncedDiscountCode, appliedDiscountCode, dispatch, state.formData]);

  const handleUpgradePlan = useCallback(
    async (plan: Plan) => {
      setIsLoadingQuote(true);

      try {
        const members =
          plan === 'Platinum'
            ? upgradePlatinum(state.formData.members ?? [])
            : upgradePediatrics(
                state.formData.members ?? [],
                state.formData.location_has_pediatrics ?? false
              );

        const updateFormData = { ...state.formData, members: members };

        // Recalculate price with the promo code
        const result = await calculatePrice(
          updateFormData,
          state.formData.discount_code
        );
        setQuote(result);

        // Save discount code to state
        dispatch({
          type: 'SET_FORM_DATA',
          payload: { members },
        });
      } catch (error) {
        console.error('Error applying discount:', error);
        setQuoteError(`Failed to upgrade to ${plan} plan. Please try again.`);
      } finally {
        setIsLoadingQuote(false);
      }
    },
    [dispatch, state.formData]
  );

  const handleContinueToCheckout = useCallback(async () => {
    setIsCheckingOut(true);
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const invoiceId = await initiateCheckout(state);
      router.push(`/get-started/checkout?invoiceId=${invoiceId}`);
    } catch (error) {
      console.error('Error initiating checkout:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to proceed to checkout. Please try again.',
      });
      setIsCheckingOut(false);
    }
  }, [state, router, dispatch]);

  const handleEditQuote = useCallback(() => goToStep('quote-edit'), [goToStep]);

  return {
    members,
    totalPrice,
    discountedPrice,
    isPromotionUsed,
    discountPercentage,
    plans,
    isLoadingQuote,
    quoteError,
    isCheckingOut,
    discountCode,
    isApplyingDiscount,
    isApplyButtonEnabled,
    locationHasPediatrics: state.formData.location_has_pediatrics ?? false,
    getMemberPrice,
    handleDiscountCodeChange,
    handleApplyDiscount,
    handleUpgradePlan,
    discountError,
    handleContinueToCheckout,
    handleEditQuote,
  };
};
